import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import qs from 'qs';
import { RouteComponentProps } from 'react-router-dom';
import { isEqual } from 'lodash';
import {
  AdminDashboardTab,
  AdminDashboardTableHeaderRowValueInterface,
  AttributesInterface,
  DateAttributesInterface,
  Option,
  SellerSearchState,
  UserData,
} from './admin-dashboard-tables/interfaces';
import AdminDashboardTable from './admin-dashboard-tables/AdminDashboardTable';

const createURL = (state) => `?${qs.stringify(state)}`;

const searchStateToUrl = (location: { pathname: string }, searchState: SellerSearchState) => {
  if (searchState) {
    return `${location.pathname}${createURL(searchState)}`;
  }

  return '';
};

const urlToSearchState = (location?: { search: string }): SellerSearchState|{} => {
  return location ? qs.parse(location.search.slice(1)) : {};
};

interface attrToLock {
  name: string,
  value: any,
  type: 'numeric'|'facet'|'query',
}

interface Props extends RouteComponentProps {
  currentUser: UserData,
  attributesToLock: Array<attrToLock>,
  externalQueryParams: Array<{key: string, value: string}>,
  indexName: string,
  tableHeaderRowValues?: AdminDashboardTableHeaderRowValueInterface[],
  customHits: any, // TODO: update `any`
  attributes: Array<AttributesInterface>,
  dateAttributes: Array<DateAttributesInterface>,
  externalSearchState?: SellerSearchState,
  tabs: Array<AdminDashboardTab>,
  bulkActions?: Array<Option>,
  allSelected?: boolean,
  setHits?: (hits: Array<number>) => void,
  toggleAllSelected?: (allSelected: boolean) => void,
  setBulkAction?: (action: Option) => void,
  bulkAction?: Option,
  useCards?: boolean,
  allowSetHitsPerPage?: boolean,
  showSearch?: boolean,
  syncURL?: boolean,
}

const SellerURLSync: React.FC<Props> = ({
  currentUser,
  attributesToLock,
  setBulkAction,
  bulkAction,
  externalQueryParams,
  indexName,
  tableHeaderRowValues,
  customHits,
  location,
  history,
  match,
  attributes,
  dateAttributes,
  externalSearchState,
  tabs,
  bulkActions,
  allSelected,
  setHits,
  toggleAllSelected,
  useCards,
  allowSetHitsPerPage,
  showSearch,
  syncURL,
}) => {
  const [facetFilters, setFacetFilters] = useState<Array<string|Array<string>>>(
    externalSearchState
      && externalSearchState.configure
      && externalSearchState.configure.facetFilters
      ? externalSearchState.configure.facetFilters
      : [],
  );
  const [numericFilters, setNumericFilters] = useState<Array<string>>(
    externalSearchState
    && externalSearchState.configure
    && externalSearchState.configure.numericFilters
      ? externalSearchState.configure.numericFilters
      : [],
  );
  const [searchState, setSearchState] = useState<SellerSearchState>(
    urlToSearchState(syncURL ? location : undefined),
  );
  const [lastLocation, setLastLocation] = useState(location);
  const [initialSearchState] = useState(
    urlToSearchState(syncURL ? location : undefined),
  );

  useEffect(() => {
    // When new filters are added, reset search to page 1
    if (searchState.page !== 1) {
      setSearchState((s) => ({ ...s, page: 1 }));
    }
  }, [facetFilters, numericFilters]);

  useEffect(() => {
    if (externalSearchState) {
      const newFacetFilters: Array<string|Array<string>> = [];
      const newNumericFilters: Array<string> = [];
      if (searchState.configure && searchState.configure.facetFilters) {
        searchState.configure.facetFilters
          .forEach((fac: string|Array<string>) => newFacetFilters.push(fac));
      }

      if (searchState.configure && searchState.configure.numericFilters) {
        searchState.configure.numericFilters
          .forEach((fac:string) => newNumericFilters.push(fac));
      }

      if (externalSearchState.configure && externalSearchState.configure.facetFilters) {
        externalSearchState.configure.facetFilters
          .forEach((fac:string|Array<string>) => newFacetFilters.push(fac));
      }
      if (externalSearchState.configure && externalSearchState.configure.numericFilters) {
        externalSearchState.configure.numericFilters
          .forEach((fac:string) => newNumericFilters.push(fac));
      }

      const newSearchState = {
        ...searchState,
        configure: {
          ...searchState.configure,
          facetFilters: [] as (string|string[])[],
          numericFilters: [] as string[],
        },
      };

      // Filter out locked properties so that they cannot be set manually in queries
      if (attributesToLock.length > 0) {
        const numLocked = attributesToLock
          .filter((attr: attrToLock) => attr.type === 'numeric')
          .map((attr: attrToLock) => attr.name);
        const facLocked = attributesToLock
          .filter((attr: attrToLock) => attr.type === 'facet')
          .map((attr: attrToLock) => attr.name);
        const filteredNum = newNumericFilters.filter((attr: string) => {
          return !numLocked.some((val) => attr.includes(val));
        });
        const filteredFac = newFacetFilters.filter((attr: string|string[]) => {
          return !facLocked.some((val) => attr.includes(val));
        });
        attributesToLock.forEach((attr: attrToLock) => {
          if (attr.type === 'numeric') {
            filteredNum.push(attr.value);
          } else if (attr.type === 'facet') {
            filteredFac.push(attr.value);
          }
        });
        newSearchState.configure.facetFilters = [...new Set(filteredFac)];
        newSearchState.configure.numericFilters = [...new Set(filteredNum)];
      } else {
        newSearchState.configure.facetFilters = [...new Set(newFacetFilters)];
        newSearchState.configure.numericFilters = [...new Set(newNumericFilters)];
      }

      if (!isEqual(newSearchState, searchState)) {
        setSearchState(newSearchState);
      }
    }
  }, [searchState]);

  useEffect(() => {
    if (location !== lastLocation) {
      setSearchState(urlToSearchState(location));
      setLastLocation(location);
    }
  }, [lastLocation, setSearchState, setLastLocation]);

  useEffect(() => {
    setSearchState(initialSearchState);
  }, [initialSearchState]);

  useEffect(() => {
    const { configure } = searchState;
    if (configure) {
      const { facetFilters: fF, numericFilters: nF } = configure;
      if (fF) {
        setFacetFilters(fF);
      }
      if (nF) {
        setNumericFilters(nF);
      }
    }
  }, [searchState, setFacetFilters, setNumericFilters]);

  const onSearchStateChange = useCallback((newSearchState) => {
    if (!isEqual(searchState, newSearchState)) {
      if (syncURL) {
        history.push(
          searchStateToUrl(location, newSearchState),
          newSearchState,
        );
      }
      setSearchState(newSearchState);
    }
  }, [searchState, setSearchState, history, searchStateToUrl, syncURL]);

  const onReset = useCallback(() => {
    setFacetFilters([]);
    setNumericFilters([]);
    setSearchState({
      configure: {
        facetFilters: [],
        hitsPerPage: 50,
        numericFilters: [],
      },
      query: '',
      page: 1,
      sortBy: indexName,
    });
    const query = externalQueryParams
      .map((param) => `${param.key}=${param.value}`)
      .join('&');
    // console.log(query);
    if (syncURL) {
      history.push(query ? `${location.pathname}?${query}` : location.pathname);
    }
  }, [
    setSearchState,
    setNumericFilters,
    setSearchState,
    indexName,
    history,
    location,
    syncURL,
  ]);

  return (
    <AdminDashboardTable
      showSearch={showSearch}
      externalQueryParams={externalQueryParams}
      searchState={searchState}
      onSearchStateChange={onSearchStateChange}
      createURL={createURL}
      history={history}
      location={location}
      facetFilters={facetFilters}
      setFacetFilters={setFacetFilters}
      numericFilters={numericFilters}
      setNumericFilters={setNumericFilters}
      indexName={indexName}
      tableHeaderRowValues={tableHeaderRowValues}
      allSelected={allSelected}
      toggleAllSelected={toggleAllSelected}
      customHits={customHits}
      match={match}
      attributes={attributes}
      dateAttributes={dateAttributes}
      onReset={onReset}
      tabs={tabs}
      setBulkAction={setBulkAction}
      bulkAction={bulkAction}
      bulkActions={bulkActions}
      setHits={setHits}
      useCards={useCards}
      allowSetHitsPerPage={allowSetHitsPerPage}
      currentUser={currentUser}
    />
  );
};

SellerURLSync.defaultProps = {
  attributes: [],
  dateAttributes: [],
  externalQueryParams: [],
  allowSetHitsPerPage: true,
  showSearch: true,
  syncURL: true,
};

export default SellerURLSync;
